import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'hi';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Complete translations for the website
const translations: Record<Language, Record<string, string>> = {
  en: {
    'home': 'Home',
    'about': 'About Us',
    'services': 'Our Services',
    'donate': 'Donate Us',
    'volunteer': 'Volunteer',
    'events': 'Events',
    'blog': 'Blog & News',
    'contact': 'Contact Us',
    'welcome': 'Welcome to Vishwa Bandhu Welfare Foundation',
    'donateNow': 'Donate Us',
    'welfareFoundation': 'Welfare Foundation',
    'quickLinks': 'Quick Links',
    'ourServices': 'Our Services',
    'treePlantation': 'Tree Plantation',
    'cowWelfare': 'Cow Welfare',
    'girlsEducation': 'Girls Education',
    'disasterRelief': 'Natural Disaster Relief',
    'oldAgeCare': 'Old Age Care',
    'contactUs': 'Contact Us',
    'english': 'English',
    'hindi': 'हिन्दी',
    // Home Page - Services Section
    'ourServicesCauses': 'Our Services & Causes',
    'servicesSubtitle': 'Comprehensive welfare programs addressing various social and environmental needs',
    'learnMore': 'Learn More',
    'viewAllServices': 'View All Services',
    // Home Page - Impact Stats
    'ourImpactNumbers': 'Our Impact in Numbers',
    'impactSubtitle': 'Making a tangible difference in communities through our various initiatives',
    'treesPlanted': 'Trees Planted',
    'girlsSupported': 'Girls Supported',
    'cowsRescued': 'Cows Rescued',
    'disasterReliefCampaigns': 'Natural Disaster Relief Campaigns',
    'oldAgeResidents': 'Old Age Home Residents',
    // Service Titles and Descriptions
    'serviceTreeTitle': 'Research and development in tree plantation',
    'serviceTreeDesc': 'Environmental protection campaigns and plantation drives',
    'serviceCowTitle': 'Cow Welfare',
    'serviceCowDesc': 'Animal rescue programs and cow adoption initiatives',
    'serviceGirlsTitle': 'Orphanage House',
    'serviceGirlsDesc': 'Education support and skill development programs',
    'serviceDisasterTitle': 'Natural Disaster Relief',
    'serviceDisasterDesc': 'Emergency rescue operations and medical assistance',
    'serviceOldageTitle': 'Old age house',
    'serviceOldageDesc': 'Elderly care programs and daily living assistance',
    'serviceMeditationTitle': 'Meditation Research',
    'serviceMeditationDesc': 'Wellness programs and research projects',
    'serviceHeritageTitle': 'Heritage (historical) & Old devotional places development',
    'serviceHeritageDesc': 'Preservation of historical places and temples',
    'serviceLegalTitle': 'Legal Aid',
    'serviceLegalDesc': 'Free legal consultation and social justice initiatives',
    'serviceOrganicTitle': 'Organic Farming',
    'serviceOrganicDesc': 'Sustainable agriculture and Ayurvedic programs',
    // Services Page Specific
    'transformingLives': 'Transforming Lives',
    'keyActivities': 'Key Activities',
    'impactAchieved': 'Impact Achieved',
    'howGetInvolved': 'How You Can Get Involved',
    'howGetInvolvedDesc': 'Join us in making a difference through various participation opportunities',
    'donateTitle': 'Donate',
    'donateDesc': 'Support our initiatives through one-time or monthly donations. Every contribution makes a difference.',
    'volunteerTitle': 'Volunteer',
    'volunteerDesc': 'Join our volunteer network and contribute your time and skills to meaningful causes.',
    'joinAsVolunteer': 'Join as Volunteer',
    'partnerTitle': 'Partner',
    'partnerDesc': 'Collaborate with us as a corporate partner or community organization to amplify our impact.',
    'partnerWithUs': 'Partner With Us',
    'ourFocusAreas': 'Our Focus Areas',
    'focusAreasDesc': 'Comprehensive approach to social welfare and community development',
    'environmental': 'Environmental',
    'environmentalDesc': 'Tree Plantation, Organic Farming',
    'education': 'Education',
    'educationDesc': 'Girls Welfare, Scholarships',
    'humanitarian': 'Humanitarian',
    'humanitarianDesc': 'Natural Disaster Relief, Elderly Care',
    'legalCultural': 'Legal & Cultural',
    'legalCulturalDesc': 'Legal Aid, Heritage Preservation',
    // About Page
    'aboutVishwaBandhu': 'About Vishwa Bandhu',
    'aboutSubtitle': 'A trusted welfare foundation committed to creating positive social change through compassionate service and sustainable development initiatives.',
    'ourStory': 'Our Story',
    'ourStoryP1': 'Founded in 2019 with Experience since 1972, Vishwa Bandhu Welfare Foundation emerged from a simple yet powerful vision: to create a world where every individual has access to basic necessities, dignity, and opportunities for growth.',
    'ourStoryP2': 'What started as a small community initiative has grown into a nationally recognized organization impacting thousands of lives across multiple states in India.',
    'ourStoryP3': 'Our journey has been guided by the principle of "Vasudhaiva Kutumbakam" - the world is one family. This philosophy drives our commitment to serve humanity without boundaries.',
    'yearsOfService': 'Years of Service',
    'citiesReached': 'Cities Reached',
    'projectsCompleted': 'Projects Completed',
    'livesImpacted': 'Lives Impacted',
    'yearsOfExperience': 'Years of Experience',
    'yearsExpDesc': 'We partner with over 320 amazing projects worldwide, and have given over $150 million in cash and product grants to other groups since 2011. We also operate our own dynamic suite of Signature Programs.',
    'whatWeDo': 'What We Do',
    'whatWeDoDesc': 'Idea of denouncing pleasure and praising pain was born and I will give you a complete account of the system, and expound the actually teachings of the great explorer of the truth.',
    'howItWorks': 'How It Works',
    'howItWorksDesc': 'Who loves or pursues or desires to obtain pain of itself, because it is pain, because occasionally circumstances occur which toil and pain can procure him some great pleasure.',
    'ourCoreValues': 'Our Core Values',
    'coreValuesSubtitle': 'The principles that guide every action and decision at Vishwa Bandhu',
    'envConservation': 'Environmental Conservation',
    'selfKnowledge': 'Self-Knowledge & Meditation',
    'sustainableAg': 'Sustainable Agriculture',
    'heritagePreserv': 'Heritage & Cultural Preservation',
    'animalWelfare': 'Animal Welfare & Shelters',
    'ayurvedicMed': 'Ayurvedic Medicine & Tribal Welfare with R&D',
    'orphanOldAge': 'Orphan & Old Age Integration',
    'founderMessage': 'Message from Founder',
    'founder': 'Founder & Spiritual Guide',
    'spiritualLeader': 'Spiritual Leader & Welfare Advocate',
    'ourLeadership': 'Our Leadership Team',
    'leadershipSubtitle': 'Dedicated professionals committed to driving positive change',
    'regLegalInfo': 'Registration & Legal Information',
    'legalStatus': 'Legal Status',
    'certifications': 'Certifications',
    'annualReports': 'Annual Reports',
    'annualReportsDesc': 'Download our annual reports for detailed information about our finances, activities, and impact',
    'download2025': 'Download 2025 Report',
    'download2024': 'Download 2024 Report',
    'download2023': 'Download 2023 Report',
    // Core Values Descriptions
    'envConservationDesc': 'If there has been any biggest exploitation of nature in the earth, then it has happened to our vegetation and our forests, trees and plants. A hundred years ago, about 77% were forests. This has happened due to the increase in population in the world. Due to which there is lack of sufficient amount of oxygen in the environment. In view of these, our patriots have resolved to plant trees which can replenish our oxygen wherever useful or unusable land for the environment.',
    'selfKnowledgeDesc': 'At this time every person is confused, even the poorest of the poor and the richest of the rich are not able to think what to do now, for this the institution will open a meditation center. So that the person understands who he is. It will be self-knowledge.',
    'sustainableAgDesc': 'In the present time, along with development, many new diseases have also developed, the root cause of this is the use of chemical fertilizers in our food and food items. In view of this, the organization has always taken the initiative to manufacture plants using organic fertilizers and bring them to the fore.',
    'heritagePreservDesc': 'India is famous for its cultural heritage for thousands of years. Our unmatched thousands of years old forts, temples, wells, stepwells and gardens, which are often being destroyed at this time, for their historical analysis and maintenance by research, to do research for its construction and not only in India but also at world level. To propagate by taking it in.',
    'animalWelfareDesc': 'India is all Hindu, Muslim, Sikh, Christian in the year. Our society and our government are worried about the cow, neither of them are able to find a permanent solution to this problem, for the eradication of this problem, the organization has made arrangements for cowshed and sheep and goat rearing all over India, which will work better.',
    'ayurvedicMedDesc': 'We are proud of allopathic medicines, but in their alternative, Ayurvedic medicines are not able to provide full publicity in the world. Our organization will produce local herbs in the forests with the help of tribals so that their economic condition can be strengthened.',
    'orphanOldAgeDesc': 'If the question of orphan children arises, then everyone is ready to cooperate, but they do not understand that an orphan remains an orphan at the time of birth and orphan at the time of death. After a thorough study, it has been decided that no child will remain an orphan after coming to the institution. He will get both the parents, so that he will become a family, by opening old age ashram with orphans and will run them automatically.',
    // Founder Message
    'founderQuote1': '"There is no dearth of patriots in our country, but the irony is that all the good high thinking people are isolated, good doctors, high level engineers, good judges and lawyers and social workers are very much for the country. We want to do something but we are not able to dare due to being alone, in view of this, we patriots united to serve the country and society - VISHWA BHANDHU WELFARE FOUNDATION, which takes a pledge to serve the country and serve the human body. Will do it with heart and money.',
    'founderQuote2': 'Together, under the umbrella of Vishwa Bandhu Welfare Foundation, we can create an unstoppable force for positive change. I humbly invite you to unite your skills and resources with us, so we can build a stronger, healthier, and more compassionate society for generations to come."',
    // Donation Page
    'makeADifferenceToday': 'Make a Difference Today',
    'donationHeroSubtitle': 'Your donation helps us continue our vital work in environmental conservation, education, healthcare, and humanitarian services',
    'scanAndDonate': 'Scan & Donate with QR Code',
    'scanAndDonateDesc': 'Use any UPI app to scan the QR code and make an instant donation. This is the fastest way to support our causes.',
    'instantPaymentConfirmation': 'Instant payment confirmation',
    'noRegistrationRequired': 'No registration required',
    'worksWithAllUPI': 'Works with all UPI apps',
    'autoGeneratedReceipt': 'Auto-generated receipt',
    'scanToDonate': 'Scan to Donate',
    'upiId': 'UPI ID',
    'useAnyUPIApp': 'Use any UPI app to donate',
    'makeYourDonation': 'Make Your Donation',
    'donationType': 'Donation Type',
    'oneTimeDonation': 'One-time Donation',
    'singleContribution': 'Single contribution',
    'monthlyDonation': 'Monthly Donation',
    'recurringContribution': 'Recurring contribution',
    'selectCampaign': 'Select Campaign',
    'generalFund': 'General Fund',
    'generalFundDesc': 'Support our overall operations and most urgent needs',
    'treePlantationCampaign': 'Tree Plantation',
    'treePlantationDesc': 'Plant and maintain trees for environmental conservation',
    'girlsEducationCampaign': 'Girls Education',
    'girlsEducationDesc': 'Support education and scholarships for girls',
    'cowWelfareCampaign': 'Cow Welfare',
    'cowWelfareDesc': 'Animal rescue and shelter maintenance',
    'disasterReliefCampaign': 'Natural Disaster Relief',
    'disasterReliefDesc': 'Emergency response and rehabilitation',
    'elderlyCareCampaign': 'Old Age Care',
    'elderlyCareDesc': 'Support elderly care and facilities',
    'secureEncrypted': 'Secure & Encrypted:',
    'secureEncryptedDesc': 'All transactions are protected with 256-bit SSL encryption. Your payment details are never stored on our servers.',
    'taxBenefits': 'Tax Benefits',
    'taxExemption50': '50% tax exemption on donations',
    'instantDonationReceipts': 'Instant donation receipts',
    'annualTaxCertificates': 'Annual tax exemption certificates',
    'transparentFundUtilization': 'Transparent fund utilization reports',
    'donorDashboard': 'Donor Dashboard',
    'donorDashboardDesc': 'Track your donations and manage your profile through our secure donor portal',
    'donationHistory': 'Donation History',
    'downloadReceipts': 'Download Receipts',
    'taxExemptionCertificates': 'Tax Exemption Certificates',
    'profileManagement': 'Profile Management',
  },
  hi: {
    'home': 'होम',
    'about': 'हमारे बारे में',
    'services': 'हमारी सेवाएं',
    'donate': 'दान करें',
    'volunteer': 'स्वयंसेवक बनें',
    'events': 'इवेंट्स',
    'blog': 'ब्लॉग और समाचार',
    'contact': 'संपर्क करें',
    'welcome': 'विश्व बंधु कल्याण फाउंडेशन में आपका स्वागत है',
    'donateNow': 'अभी दान करें',
    'welfareFoundation': 'कल्याण फाउंडेशन',
    'quickLinks': 'त्वरित लिंक',
    'ourServices': 'हमारी सेवाएं',
    'treePlantation': 'वृक्षारोपण',
    'cowWelfare': 'गौ कल्याण',
    'girlsEducation': 'लड़कियों की शिक्षा',
    'disasterRelief': 'आपदा राहत',
    'oldAgeCare': 'वृद्ध देखभाल',
    'contactUs': 'हमसे संपर्क करें',
    'english': 'English',
    'hindi': 'हिन्दी',
    // Home Page - Services Section
    'ourServicesCauses': 'हमारी सेवाएं और कार्य',
    'servicesSubtitle': 'विभिन्न सामाजिक और पर्यावरणीय आवश्यकताओं को संबोधित करने वाले व्यापक कल्याण कार्यक्रम',
    'learnMore': 'और जानें',
    'viewAllServices': 'सभी सेवाएं देखें',
    // Home Page - Impact Stats
    'ourImpactNumbers': 'संख्या में हमारा प्रभाव',
    'impactSubtitle': 'हमारी विभिन्न पहलों के माध्यम से समुदायों में ठोस अंतर बनाना',
    'treesPlanted': 'वृक्ष लगाए गए',
    'girlsSupported': 'लड़कियों को समर्थन दिया',
    'cowsRescued': 'गायों को बचाया गया',
    'disasterReliefCampaigns': 'प्राकृतिक आपदा राहत अभियान',
    'oldAgeResidents': 'वृद्धाश्रम निवासी',
    // Service Titles and Descriptions
    'serviceTreeTitle': 'वृक्षारोपण में अनुसंधान और विकास',
    'serviceTreeDesc': 'पर्यावरण संरक्षण अभियान और वृक्षारोपण अभियान',
    'serviceCowTitle': 'गौ कल्याण',
    'serviceCowDesc': 'पशु बचाव कार्यक्रम और गाय गोद लेने की पहल',
    'serviceGirlsTitle': 'अनाथालय',
    'serviceGirlsDesc': 'शिक्षा सहायता और कौशल विकास कार्यक्रम',
    'serviceDisasterTitle': 'प्राकृतिक आपदा राहत',
    'serviceDisasterDesc': 'आपातकालीन बचाव कार्यक्रम और चिकित्सा सहायता',
    'serviceOldageTitle': 'वृद्धाश्रम',
    'serviceOldageDesc': 'बुजुर्गों की देखभाल कार्यक्रम और दैनिक जीवन सहायता',
    'serviceMeditationTitle': 'ध्यान अनुसंधान',
    'serviceMeditationDesc': 'कल्याण कार्यक्रम और अनुसंधान परियोजनाएं',
    'serviceHeritageTitle': 'विरासत (ऐतिहासिक) और पुराने धार्मिक स्थलों का विकास',
    'serviceHeritageDesc': 'ऐतिहासिक स्थलों और मंदिरों का संरक्षण',
    'serviceLegalTitle': 'कानूनी सहायता',
    'serviceLegalDesc': 'निःशुल्क कानूनी परामर्श और सामाजिक न्याय पहल',
    'serviceOrganicTitle': 'जैविक खेती',
    'serviceOrganicDesc': 'सतत कृषि और आयुर्वेदिक कार्यक्रम',
    // Services Page Specific
    'transformingLives': 'जीवन बदलना',
    'keyActivities': 'मुख्य गतिविधियां',
    'impactAchieved': 'प्रभाव प्राप्त',
    'howGetInvolved': 'आप कैसे शामिल हो सकते हैं',
    'howGetInvolvedDesc': 'विभिन्न भागीदारी के अवसरों के माध्यम से अंतर बनाने में हमारे साथ शामिल हों',
    'donateTitle': 'दान करें',
    'donateDesc': 'एकबारगी या मासिक दान के माध्यम से हमारी पहलों का समर्थन करें। हर योगदान अंतर लाता है।',
    'volunteerTitle': 'स्वयंसेवक',
    'volunteerDesc': 'हमारे स्वयंसेवक नेटवर्क में शामिल हों और सार्थक कारणों में अपना समय और कौशल योगदान करें।',
    'joinAsVolunteer': 'स्वयंसेवक के रूप में शामिल हों',
    'partnerTitle': 'साझेदार',
    'partnerDesc': 'हमारे प्रभाव को बढ़ाने के लिए कॉर्पोरेट साझेदार या सामुदायिक संगठन के रूप में हमारे साथ सहयोग करें।',
    'partnerWithUs': 'हमारे साथ साझेदारी करें',
    'ourFocusAreas': 'हमारे फोकस क्षेत्र',
    'focusAreasDesc': 'सामाजिक कल्याण और सामुदायिक विकास के लिए व्यापक दृष्टिकोण',
    'environmental': 'पर्यावरणीय',
    'environmentalDesc': 'वृक्षारोपण, जैविक खेती',
    'education': 'शिक्षा',
    'educationDesc': 'लड़कियों का कल्याण, छात्रवृत्ति',
    'humanitarian': 'मानवीय',
    'humanitarianDesc': 'प्राकृतिक आपदा राहत, बुजुर्गों की देखभाल',
    'legalCultural': 'कानूनी और सांस्कृतिक',
    'legalCulturalDesc': 'कानूनी सहायता, विरासत संरक्षण',
    // About Page
    'aboutVishwaBandhu': 'विश्व बंधु के बारे में',
    'aboutSubtitle': 'एक विश्वसनीय कल्याण फाउंडेशन जो दयालु सेवा और सतत विकास पहल के माध्यम से सकारात्मक सामाजिक परिवर्तन बनाने के लिए प्रतिबद्ध है।',
    'ourStory': 'हमारी कहानी',
    'ourStoryP1': '1972 के अनुभव के साथ 2019 में स्थापित, विश्व बंधु कल्याण फाउंडेशन एक सरल लेकिन शक्तिशाली दृष्टि से उभरा: एक ऐसी दुनिया बनाना जहां हर व्यक्ति को बुनियादी आवश्यकताओं, गरिमा और विकास के अवसरों तक पहुंच हो।',
    'ourStoryP2': 'जो एक छोटी सामुदायिक पहल के रूप में शुरू हुआ, वह भारत के कई राज्यों में हजारों जीवन को प्रभावित करने वाले राष्ट्रीय स्तर पर मान्यता प्राप्त संगठन के रूप में विकसित हुआ है।',
    'ourStoryP3': 'हमारी यात्रा "वसुधैव कुटुम्बकम" के सिद्धांत से निर्देशित रही है - विश्व एक परिवार है। यह दर्शन बिना सीमाओं के मानवता की सेवा करने की हमारी प्रतिबद्धता को संचालित करता है।',
    'yearsOfService': 'सेवा के वर्ष',
    'citiesReached': 'शहरों तक पहुंच',
    'projectsCompleted': 'पूर्ण परियोजनाएं',
    'livesImpacted': 'प्रभावित जीवन',
    'yearsOfExperience': 'अनुभव के वर्ष',
    'yearsExpDesc': 'हम दुनिया भर में 320 से अधिक अद्भुत परियोजनाओं के साथ साझेदारी करते हैं, और 2011 से अन्य समूहों को नकद और उत्पाद अनुदान में $150 मिलियन से अधिक दिए हैं। हम अपने स्वयं के गतिशील हस्ताक्षर कार्यक्रमों का भी संचालन करते हैं।',
    'whatWeDo': 'हम क्या करते हैं',
    'whatWeDoDesc': 'आनंद की निंदा करने और दर्द की प्रशंसा करने का विचार पैदा हुआ था और मैं आपको प्रणाली का पूरा विवरण दूंगा, और सत्य के महान खोजकर्ता की शिक्षाओं को वास्तव में समझाऊंगा।',
    'howItWorks': 'यह कैसे काम करता है',
    'howItWorksDesc': 'जो दर्द को प्यार करता है या पाने की इच्छा रखता है, क्योंकि यह दर्द है, क्योंकि कभी-कभी परिस्थितियां होती हैं जो परिश्रम और दर्द उसे कुछ महान खुशी प्रदान कर सकती हैं।',
    'ourCoreValues': 'हमारे मूल मूल्य',
    'coreValuesSubtitle': 'वे सिद्धांत जो विश्व बंधु में हर कार्रवाई और निर्णय का मार्गदर्शन करते हैं',
    'envConservation': 'पर्यावरण संरक्षण',
    'selfKnowledge': 'आत्म-ज्ञान और ध्यान',
    'sustainableAg': 'सतत कृषि',
    'heritagePreserv': 'विरासत और सांस्कृतिक संरक्षण',
    'animalWelfare': 'पशु कल्याण और आश्रय',
    'ayurvedicMed': 'आयुर्वेदिक चिकित्सा और आदिवासी कल्याण अनुसंधान के साथ',
    'orphanOldAge': 'अनाथ और वृद्धाश्रम एकीकरण',
    'founderMessage': 'संस्थापक का संदेश',
    'founder': 'संस्थापक और आध्यात्मिक मार्गदर्शक',
    'spiritualLeader': 'आध्यात्मिक नेता और कल्याण अधिवक्ता',
    'ourLeadership': 'हमारी नेतृत्व टीम',
    'leadershipSubtitle': 'सकारात्मक परिवर्तन लाने के लिए समर्पित पेशेवर',
    'regLegalInfo': 'पंजीकरण और कानूनी जानकारी',
    'legalStatus': 'कानूनी स्थिति',
    'certifications': 'प्रमाणपत्र',
    'annualReports': 'वार्षिक रिपोर्ट',
    'annualReportsDesc': 'हमारे वित्त, गतिविधियों और प्रभाव के बारे में विस्तृत जानकारी के लिए हमारी वार्षिक रिपोर्ट डाउनलोड करें',
    'download2025': '2025 रिपोर्ट डाउनलोड करें',
    'download2024': '2024 रिपोर्ट डाउनलोड करें',
    'download2023': '2023 रिपोर्ट डाउनलोड करें',
    // Core Values Descriptions
    'envConservationDesc': 'यदि पृथ्वी में प्रकृति का कोई सबसे बड़ा शोषण हुआ है, तो वह हमारी वनस्पति और हमारे जंगलों, पेड़ों और पौधों के साथ हुआ है। सौ साल पहले, लगभग 77% जंगल थे। यह दुनिया में जनसंख्या में वृद्धि के कारण हुआ है। जिसके कारण वातावरण में पर्याप्त मात्रा में ऑक्सीजन की कमी है। इन्हें देखते हुए, हमारे देशभक्तों ने पेड़ लगाने का संकल्प लिया है जो पर्यावरण के लिए उपयोगी या बेकार भूमि जहाँ भी हो, वहाँ हमारी ऑक्सीजन को फिर से भर सकते हैं।',
    'selfKnowledgeDesc': 'इस समय हर व्यक्ति भ्रमित है, गरीब से गरीब और अमीर से अमीर भी यह नहीं सोच पा रहे हैं कि अब क्या करें, इसके लिए संस्था एक ध्यान केंद्र खोलेगी। ताकि व्यक्ति समझे कि वह कौन है। यह आत्म-ज्ञान होगा।',
    'sustainableAgDesc': 'वर्तमान समय में विकास के साथ-साथ कई नई बीमारियां भी विकसित हुई हैं, इसका मूल कारण हमारे भोजन और खाद्य पदार्थों में रासायनिक उर्वरकों का उपयोग है। इसे देखते हुए, संगठन ने हमेशा जैविक उर्वरकों का उपयोग करके पौधों का निर्माण करने और उन्हें सामने लाने की पहल की है।',
    'heritagePreservDesc': 'भारत हजारों वर्षों से अपनी सांस्कृतिक विरासत के लिए प्रसिद्ध है। हमारे हजारों साल पुराने किले, मंदिर, कुएं, बावड़ियां और बगीचे, जो इस समय अक्सर नष्ट हो रहे हैं, उनके ऐतिहासिक विश्लेषण और अनुसंधान द्वारा रखरखाव के लिए, उनके निर्माण के लिए अनुसंधान करने और न केवल भारत में बल्कि विश्व स्तर पर इसे ले जाकर प्रचार करना।',
    'animalWelfareDesc': 'भारत में सभी हिंदू, मुस्लिम, सिख, ईसाई हैं। हमारा समाज और हमारी सरकार गाय के बारे में चिंतित हैं, उनमें से कोई भी इस समस्या का स्थायी समाधान खोजने में सक्षम नहीं है, इस समस्या के उन्मूलन के लिए, संगठन ने पूरे भारत में गौशाला और भेड़ और बकरी पालन की व्यवस्था की है, जो बेहतर काम करेगी।',
    'ayurvedicMedDesc': 'हम एलोपैथिक दवाओं पर गर्व करते हैं, लेकिन उनके विकल्प में, आयुर्वेदिक दवाएं दुनिया में पूर्ण प्रचार प्रदान करने में सक्षम नहीं हैं। हमारी संस्था आदिवासियों की मदद से जंगलों में स्थानीय जड़ी-बूटियों का उत्पादन करेगी ताकि उनकी आर्थिक स्थिति मजबूत हो सके।',
    'orphanOldAgeDesc': 'यदि अनाथ बच्चों का सवाल उठता है, तो हर कोई सहयोग के लिए तैयार है, लेकिन वे यह नहीं समझते हैं कि एक अनाथ जन्म के समय अनाथ रहता है और मृत्यु के समय अनाथ रहता है। गहन अध्ययन के बाद, यह निर्णय लिया गया है कि संस्था में आने के बाद कोई भी बच्चा अनाथ नहीं रहेगा। उसे दोनों माता-पिता मिलेंगे, ताकि वह एक परिवार बन जाए, अनाथों के साथ वृद्धाश्रम खोलकर और उन्हें स्वचालित रूप से चलाएगा।',
    // Founder Message
    'founderQuote1': '"हमारे देश में देशभक्तों की कोई कमी नहीं है, लेकिन विडंबना यह है कि सभी अच्छे उच्च विचारधारा वाले लोग अलग-थलग हैं, अच्छे डॉक्टर, उच्च स्तरीय इंजीनियर, अच्छे न्यायाधीश और वकील और सामाजिक कार्यकर्ता देश के लिए बहुत कुछ हैं। हम कुछ करना चाहते हैं लेकिन अकेले होने के कारण हिम्मत नहीं कर पा रहे हैं, इसे देखते हुए हम देशभक्तों ने देश और समाज की सेवा के लिए एकजुट होकर - विश्व बंधु कल्याण फाउंडेशन, जो देश की सेवा करने और मानव शरीर की सेवा करने की प्रतिज्ञा लेता है। दिल और पैसे से करेंगे।',
    'founderQuote2': 'साथ में, विश्व बंधु कल्याण फाउंडेशन की छत्रछाया में, हम सकारात्मक परिवर्तन के लिए एक अटूट शक्ति बना सकते हैं। मैं विनम्रतापूर्वक आपको अपने कौशल और संसाधनों को हमारे साथ एकजुट करने के लिए आमंत्रित करता हूं, ताकि हम आने वाली पीढ़ियों के लिए एक मजबूत, स्वस्थ और अधिक दयालु समाज का निर्माण कर सकें।"',
    // Donation Page
    'makeADifferenceToday': 'आज अंतर बनाएं',
    'donationHeroSubtitle': 'आपका दान पर्यावरण संरक्षण, शिक्षा, स्वास्थ्य सेवा और मानवीय सेवाओं में हमारे महत्वपूर्ण कार्य को जारी रखने में मदद करता है',
    'scanAndDonate': 'QR कोड से स्कैन करें और दान करें',
    'scanAndDonateDesc': 'तत्काल दान करने के लिए QR कोड को स्कैन करने के लिए किसी भी UPI ऐप का उपयोग करें। यह हमारे कारणों का समर्थन करने का सबसे तेज़ तरीका है।',
    'instantPaymentConfirmation': 'तत्काल भुगतान पुष्टि',
    'noRegistrationRequired': 'कोई पंजीकरण आवश्यक नहीं',
    'worksWithAllUPI': 'सभी UPI ऐप्स के साथ काम करता है',
    'autoGeneratedReceipt': 'स्वतः जनरेट रसीद',
    'scanToDonate': 'दान के लिए स्कैन करें',
    'upiId': 'UPI ID',
    'useAnyUPIApp': 'दान करने के लिए किसी भी UPI ऐप का उपयोग करें',
    'makeYourDonation': 'अपना दान करें',
    'donationType': 'दान का प्रकार',
    'oneTimeDonation': 'एक बार का दान',
    'singleContribution': 'एकल योगदान',
    'monthlyDonation': 'मासिक दान',
    'recurringContribution': 'आवर्ती योगदान',
    'selectCampaign': 'अभियान चुनें',
    'generalFund': 'सामान्य फंड',
    'generalFundDesc': 'हमारे समग्र संचालन और सबसे जरूरी जरूरतों का समर्थन करें',
    'treePlantationCampaign': 'वृक्षारोपण',
    'treePlantationDesc': 'पर्यावरण संरक्षण के लिए पेड़ लगाएं और उनका रखरखाव करें',
    'girlsEducationCampaign': 'लड़कियों की शिक्षा',
    'girlsEducationDesc': 'लड़कियों के लिए शिक्षा और छात्रवृत्ति का समर्थन करें',
    'cowWelfareCampaign': 'गौ कल्याण',
    'cowWelfareDesc': 'पशु बचाव और आश्रय रखरखाव',
    'disasterReliefCampaign': 'प्राकृतिक आपदा राहत',
    'disasterReliefDesc': 'आपातकालीन प्रतिक्रिया और पुनर्वास',
    'elderlyCareCampaign': 'वृद्धाश्रम देखभाल',
    'elderlyCareDesc': 'बुजुर्गों की देखभाल और सुविधाओं का समर्थन करें',
    'secureEncrypted': 'सुरक्षित और एन्क्रिप्टेड:',
    'secureEncryptedDesc': 'सभी लेनदेन 256-बिट SSL एन्क्रिप्शन के साथ सुरक्षित हैं। आपकी भुगतान विवरण कभी भी हमारे सर्वर पर संग्रहीत नहीं होती हैं।',
    'taxBenefits': 'कर लाभ',
    'taxExemption50': 'दान पर 50% कर छूट',
    'instantDonationReceipts': 'तत्काल दान रसीदें',
    'annualTaxCertificates': 'वार्षिक कर छूट प्रमाणपत्र',
    'transparentFundUtilization': 'पारदर्शी फंड उपयोग रिपोर्ट',
    'donorDashboard': 'दाता डैशबोर्ड',
    'donorDashboardDesc': 'हमारे सुरक्षित दाता पोर्टल के माध्यम से अपने दान को ट्रैक करें और अपनी प्रोफाइल को प्रबंधित करें',
    'donationHistory': 'दान इतिहास',
    'downloadReceipts': 'रसीदें डाउनलोड करें',
    'taxExemptionCertificates': 'कर छूट प्रमाणपत्र',
    'profileManagement': 'प्रोफाइल प्रबंधन',
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prevLanguage => prevLanguage === 'en' ? 'hi' : 'en');
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};